import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Textarea } from "../../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "../../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { 
  AlertTriangle, Upload, Loader2, FileText, Download, Calendar, 
  CheckCircle2, XCircle, Clock, Info, Sparkles, Eye, ArrowLeft, RotateCcw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SIGNATAIRE = {
  signataire_nom: "Dov Averdy",
  signataire_fonction: "PDG",
  signataire_tel: "06 23 14 97 44",
  signataire_email: "dov@betaye.fr"
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR");
  } catch {
    return dateStr;
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

/**
 * Carte "Résiliation" affichée dans la fiche contrat Clarix
 */
export function ResiliationClientCard({ contract, onRefresh, token }) {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  const isEnCours = contract.statut_resiliation === "resiliation_en_cours";
  const isResilie = contract.statut_resiliation === "resilier";
  
  const handleAnnulerResiliation = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/contracts/${contract.id}/demande-resiliation/annuler`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Résiliation annulée — le contrat redevient actif");
      if (response.data.notification_email_envoyee) {
        toast.info("Email de notification envoyé (reprise des prélèvements)");
      }
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Annulation error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l annulation");
    } finally {
      setCancelling(false);
    }
  };
  
  // Charger la liste des documents
  const fetchDocuments = async () => {
    if (!isEnCours && !isResilie) return;
    setLoadingDocs(true);
    try {
      const response = await axios.get(
        `${API}/contracts/${contract.id}/demande-resiliation/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoadingDocs(false);
    }
  };
  
  useEffect(() => {
    fetchDocuments();
  }, [contract.id, isEnCours, isResilie]);
  
  if (!isEnCours && !isResilie && !contract.date_demande_resiliation) {
    return null;
  }
  
  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await axios.get(
        `${API}/contracts/${contract.id}/demande-resiliation/document/${docId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  return (
    <Card className={`border-2 ${isEnCours ? "border-orange-400 bg-orange-50" : "border-gray-400 bg-gray-100"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className={isEnCours ? "w-5 h-5 text-orange-600" : "w-5 h-5 text-gray-600"} />
          Résiliation
          <Badge className={isEnCours ? "bg-orange-500 text-white" : "bg-gray-500 text-white"}>
            {isEnCours ? "En cours" : "Effectuée"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Date de réception</p>
            <p className="font-medium">{formatDate(contract.date_demande_resiliation)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Date d effet</p>
            <p className={`font-bold ${isEnCours ? "text-orange-700" : "text-gray-700"}`}>
              {formatDate(contract.date_effet_resiliation)}
            </p>
          </div>
        </div>
        
        {contract.reference_ar_resiliation && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Référence AR</p>
            <p className="font-mono text-sm">{contract.reference_ar_resiliation}</p>
          </div>
        )}
        
        {contract.signataire_demande_resiliation && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Signataire</p>
            <p className="text-sm">{contract.signataire_demande_resiliation}</p>
          </div>
        )}
        
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Préavis appliqué</p>
          <p className="text-sm">{contract.duree_preavis_resiliation_mois || 3} mois</p>
        </div>
        
        {contract.analyse_conditions_resiliation?.analysis && (
          <div className="pt-2 border-t" data-testid="conditions-ia-card-section">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-indigo-500" />
              Conditions vérifiées par IA
            </p>
            {contract.analyse_conditions_resiliation.base_calcul_label && (
              <p className="text-sm">Base de calcul : <strong>{contract.analyse_conditions_resiliation.base_calcul_label}</strong></p>
            )}
            {contract.analyse_conditions_resiliation.analysis.clause_resiliation_citee && (
              <p className="text-xs italic text-gray-600 mt-1">« {contract.analyse_conditions_resiliation.analysis.clause_resiliation_citee} »</p>
            )}
          </div>
        )}
        
        {/* Documents de résiliation */}
        {(documents.length > 0 || contract.documents_resiliation?.length > 0) && (
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Documents joints</p>
            <div className="space-y-1">
              {(documents.length > 0 ? documents : contract.documents_resiliation || []).map((doc, idx) => (
                <Button 
                  key={doc.id || idx}
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start text-left"
                  onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                >
                  <Download className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{doc.filename}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        
        {isEnCours && (
          <Alert className="bg-orange-100 border-orange-300">
            <Clock className="w-4 h-4 text-orange-600" />
            <AlertDescription className="text-orange-800 text-sm">
              Les prélèvements seront stoppés au <strong>{formatDateLong(contract.date_effet_resiliation)}</strong>
            </AlertDescription>
          </Alert>
        )}
        
        {isEnCours && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                data-testid="annuler-resiliation-btn"
                variant="outline" 
                size="sm"
                className="w-full border-green-300 text-green-700 hover:bg-green-50"
                disabled={cancelling}
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Annuler la résiliation (rétractation client)
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Annuler la résiliation ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Le contrat <strong>{contract.societe} — {contract.produit}</strong> redeviendra actif et les prélèvements continueront normalement.
                  Les documents de résiliation seront conservés dans le dossier « DEMANDE DE RÉSILIATION » et un email de notification sera envoyé aux destinataires configurés.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="annuler-resiliation-cancel">Retour</AlertDialogCancel>
                <AlertDialogAction 
                  data-testid="annuler-resiliation-confirm"
                  onClick={handleAnnulerResiliation}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Confirmer l annulation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Bouton + Modal pour importer une demande de résiliation client (module Contrats)
 */
export function ResiliationClientUploadContracts({ contract, onSuccess, token }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [confirmData, setConfirmData] = useState({
    date_demande_resiliation: "",
    date_effet_resiliation: "",
    reference_ar: "",
    signataire: "",
    duree_preavis_mois: contract?.duree_preavis_resiliation_mois || 3
  });
  const [filesData, setFilesData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [iaApplied, setIaApplied] = useState(false);
  const [confirmStep, setConfirmStep] = useState("infos");
  const [lettrePreview, setLettrePreview] = useState(null);
  const [lettreCorps, setLettreCorps] = useState("");
  const [signataireLettre, setSignataireLettre] = useState(DEFAULT_SIGNATAIRE);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  const fileInputRef = useRef(null);
  
  // Ne pas afficher si déjà en résiliation
  if (contract?.statut_resiliation === "resiliation_en_cours" || contract?.statut_resiliation === "resilier") {
    return null;
  }
  
  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    const validFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Type non supporté: ${file.name}`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Fichier trop volumineux: ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };
  
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Veuillez sélectionner au moins un fichier");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append("files", file);
      });
      
      const response = await axios.post(
        `${API}/contracts/${contract.id}/demande-resiliation/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      const result = response.data;
      setExtractionResult(result);
      
      setConfirmData({
        date_demande_resiliation: result.extraction?.date_lettre_client || new Date().toISOString().split("T")[0],
        date_effet_resiliation: result.calcul_date_effet?.date_effet_resiliation || "",
        reference_ar: result.extraction?.reference_ar || "",
        signataire: result.extraction?.nom_signataire 
          ? `${result.extraction.nom_signataire}${result.extraction.fonction_signataire ? " - " + result.extraction.fonction_signataire : ""}`
          : "",
        duree_preavis_mois: result.contrat?.duree_preavis_mois || 3
      });
      
      setFilesData(result.files_data || []);
      setIaApplied(false);
      setShowUploadModal(false);
      setShowConfirmModal(true);
      
      toast.success(`${result.files_count} document(s) analysé(s)`);
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l analyse");
    } finally {
      setUploading(false);
    }
  };
  
  const handlePreviewLettre = async () => {
    if (!confirmData.date_demande_resiliation || !confirmData.date_effet_resiliation) {
      toast.error("Veuillez renseigner les dates");
      return;
    }
    
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${API}/contracts/${contract.id}/demande-resiliation/preview-lettre`,
        confirmData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setLettrePreview(response.data);
      setLettreCorps(response.data.corps || "");
      setSignataireLettre(response.data.signataire || DEFAULT_SIGNATAIRE);
      setConfirmStep("lettre");
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération de l aperçu");
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const handleConfirm = async () => {
    if (!confirmData.date_demande_resiliation || !confirmData.date_effet_resiliation) {
      toast.error("Veuillez renseigner les dates");
      return;
    }
    
    setConfirming(true);
    
    try {
      // Envoyer les fichiers dans le body, pas dans l'URL
      const requestBody = {
        ...confirmData,
        files_data: filesData,
        analyse_conditions: extractionResult?.analyse_conditions || null,
        lettre_corps: lettreCorps || null,
        signataire_lettre: signataireLettre
      };
      
      const response = await axios.post(
        `${API}/contracts/${contract.id}/demande-resiliation/confirm`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity
        }
      );
      
      toast.success("Résiliation validée avec succès");
      
      if (response.data.notification_email_envoyee) {
        toast.info("Email de notification envoyé");
      }
      
      setShowConfirmModal(false);
      setExtractionResult(null);
      setFilesData([]);
      setSelectedFiles([]);
      setConfirmStep("infos");
      setLettrePreview(null);
      setLettreCorps("");
      setSignataireLettre(DEFAULT_SIGNATAIRE);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
    } catch (error) {
      console.error("Confirm error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setConfirming(false);
    }
  };
  
  const resetAndClose = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  return (
    <>
      <Button 
        variant="outline" 
        className="border-orange-300 text-orange-700 hover:bg-orange-50"
        onClick={() => setShowUploadModal(true)}
      >
        <Upload className="w-4 h-4 mr-2" />
        Importer demande de résiliation
      </Button>
      
      {/* Modal upload multiple */}
      <Dialog open={showUploadModal} onOpenChange={(open) => !open && resetAndClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-600" />
              Importer les documents de résiliation
            </DialogTitle>
            <DialogDescription>
              Importez la lettre de résiliation et/ou l enveloppe recommandée du client.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Contrat:</strong> {contract?.societe} - {contract?.produit}
              </AlertDescription>
            </Alert>
            
            {/* Zone de drop */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileText className="w-10 h-10 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Cliquez pour ajouter des fichiers</p>
              <p className="text-xs text-gray-400 mt-1">PDF, PNG ou JPG (max 10 Mo chacun)</p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                multiple
                className="hidden"
                onChange={handleFilesSelect}
              />
            </div>
            
            {/* Liste des fichiers sélectionnés */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">{selectedFiles.length} fichier(s) sélectionné(s):</p>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm truncate max-w-[250px]">{file.name}</span>
                      <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(0)} Ko)</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                <strong>Conseil:</strong> Importez l enveloppe recommandée pour prouver la date de réception (cachet de la poste).
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={resetAndClose} disabled={uploading}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || selectedFiles.length === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Analyser ({selectedFiles.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal confirmation */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {confirmStep === "infos" ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Validation de la demande de résiliation
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 text-orange-600" />
                  Aperçu de la lettre d acceptation
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 overflow-y-auto flex-1 pr-2">
            {confirmStep === "infos" && (<>
            {/* Documents importés */}
            {filesData.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-2">{filesData.length} document(s) importé(s):</p>
                <div className="flex flex-wrap gap-2">
                  {filesData.map((f, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      <FileText className="w-3 h-3 mr-1" />
                      {f.filename}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {extractionResult?.extraction && (
              <Alert className={extractionResult.extraction.confidence_score > 0.7 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
                <Info className={extractionResult.extraction.confidence_score > 0.7 ? "w-4 h-4 text-green-600" : "w-4 h-4 text-amber-600"} />
                <AlertDescription className="text-sm">
                  <strong>Extraction IA</strong> - Confiance: {Math.round((extractionResult.extraction.confidence_score || 0) * 100)}%
                </AlertDescription>
              </Alert>
            )}
            
            {extractionResult?.calcul_date_effet && (
              <Alert className="bg-blue-50 border-blue-200">
                <Calendar className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  {extractionResult.calcul_date_effet.explication}
                </AlertDescription>
              </Alert>
            )}
            
            {/* Analyse IA des documents contractuels */}
            {extractionResult?.analyse_conditions && !extractionResult.analyse_conditions.error && (
              <div data-testid="analyse-conditions-ia-panel" className="p-4 rounded-lg border-2 border-indigo-200 bg-indigo-50 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <p className="font-semibold text-indigo-900 text-sm">Analyse IA des documents contractuels</p>
                  {extractionResult.analyse_conditions.from_cache && (
                    <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-300">déjà analysés</Badge>
                  )}
                </div>
                
                <p className="text-xs text-indigo-700">
                  {(extractionResult.analyse_conditions.documents_analyses || []).length} document(s) analysé(s) : {(extractionResult.analyse_conditions.documents_analyses || []).join(", ")}
                </p>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-indigo-500 uppercase tracking-wide">Préavis détecté</p>
                    <p className="font-medium text-indigo-900" data-testid="ia-preavis-detecte">
                      {extractionResult.analyse_conditions.preavis_ia_mois} mois
                      {extractionResult.analyse_conditions.preavis_ia_mois !== confirmData.duree_preavis_mois && !iaApplied && (
                        <span className="text-orange-600 text-xs ml-1">(configuré : {confirmData.duree_preavis_mois} mois)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-500 uppercase tracking-wide">Base de calcul</p>
                    <p className="font-medium text-indigo-900" data-testid="ia-base-calcul">{extractionResult.analyse_conditions.base_calcul_label}</p>
                  </div>
                  {extractionResult.analyse_conditions.analysis?.date_installation && (
                    <div>
                      <p className="text-xs text-indigo-500 uppercase tracking-wide">Date d installation (PV)</p>
                      <p className="font-medium text-indigo-900">{formatDate(extractionResult.analyse_conditions.analysis.date_installation)}</p>
                    </div>
                  )}
                  {extractionResult.analyse_conditions.calcul_date_effet_ia?.date_effet_resiliation && (
                    <div>
                      <p className="text-xs text-indigo-500 uppercase tracking-wide">Date d effet suggérée</p>
                      <p className="font-bold text-indigo-900" data-testid="ia-date-effet-suggeree">
                        {formatDate(extractionResult.analyse_conditions.calcul_date_effet_ia.date_effet_resiliation)}
                      </p>
                    </div>
                  )}
                </div>
                
                {extractionResult.analyse_conditions.calcul_date_effet_ia?.explication && (
                  <p className="text-xs text-indigo-700">{extractionResult.analyse_conditions.calcul_date_effet_ia.explication}</p>
                )}
                
                {extractionResult.analyse_conditions.analysis?.clause_resiliation_citee && (
                  <div className="p-2 bg-white/70 rounded border border-indigo-100">
                    <p className="text-xs text-indigo-500 uppercase tracking-wide mb-1">
                      Clause citée {extractionResult.analyse_conditions.analysis.document_source ? `(${extractionResult.analyse_conditions.analysis.document_source})` : ""}
                    </p>
                    <p className="text-xs italic text-gray-700">« {extractionResult.analyse_conditions.analysis.clause_resiliation_citee} »</p>
                  </div>
                )}
                
                {extractionResult.analyse_conditions.suggestion_differente && extractionResult.analyse_conditions.calcul_date_effet_ia?.date_effet_resiliation && (
                  iaApplied ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <AlertDescription className="text-green-800 text-sm">
                        Suggestion IA appliquée aux champs ci-dessous.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Button
                      data-testid="apply-ia-suggestion-btn"
                      size="sm"
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => {
                        setConfirmData({
                          ...confirmData,
                          date_effet_resiliation: extractionResult.analyse_conditions.calcul_date_effet_ia.date_effet_resiliation,
                          duree_preavis_mois: extractionResult.analyse_conditions.preavis_ia_mois
                        });
                        setIaApplied(true);
                        toast.success("Conditions détectées par l IA appliquées");
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Appliquer la suggestion IA (effet au {formatDate(extractionResult.analyse_conditions.calcul_date_effet_ia.date_effet_resiliation)})
                    </Button>
                  )
                )}
                
                {extractionResult.analyse_conditions.suggestion_differente === false && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <AlertDescription className="text-green-800 text-sm">
                      Les conditions détectées confirment le calcul standard.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            
            {extractionResult?.analyse_conditions?.error && (
              <Alert className="bg-amber-50 border-amber-200">
                <Info className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  Analyse IA des documents contractuels impossible : {extractionResult.analyse_conditions.error}
                </AlertDescription>
              </Alert>
            )}
            
            {extractionResult && !extractionResult.analyse_conditions && (
              <Alert className="bg-gray-50 border-gray-200">
                <Info className="w-4 h-4 text-gray-500" />
                <AlertDescription className="text-gray-600 text-sm">
                  Aucun document dans « DOCUMENTS CONTRACTUELS » — calcul basé sur la configuration du contrat.
                </AlertDescription>
              </Alert>
            )}
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de réception *</Label>
                <Input 
                  type="date"
                  value={confirmData.date_demande_resiliation}
                  onChange={(e) => setConfirmData({...confirmData, date_demande_resiliation: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Cachet de la poste ou date AR</p>
              </div>
              
              <div>
                <Label>Référence AR</Label>
                <Input 
                  value={confirmData.reference_ar}
                  onChange={(e) => setConfirmData({...confirmData, reference_ar: e.target.value})}
                  placeholder="N° de suivi"
                />
              </div>
              
              <div>
                <Label>Date d effet *</Label>
                <Input 
                  type="date"
                  value={confirmData.date_effet_resiliation}
                  onChange={(e) => setConfirmData({...confirmData, date_effet_resiliation: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Préavis (mois)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="12"
                  value={confirmData.duree_preavis_mois}
                  onChange={(e) => setConfirmData({...confirmData, duree_preavis_mois: parseInt(e.target.value) || 3})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Signataire</Label>
                <Input 
                  value={confirmData.signataire}
                  onChange={(e) => setConfirmData({...confirmData, signataire: e.target.value})}
                  placeholder="Nom - Fonction"
                />
              </div>
            </div>
            
            <Alert className="bg-orange-50 border-orange-200">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <AlertDescription className="text-orange-800 text-sm">
                <strong>Actions automatiques:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li>Statut: Résiliation en cours</li>
                  <li>Génération lettre acceptation (.docx)</li>
                  <li>Notification email</li>
                </ul>
              </AlertDescription>
            </Alert>
            </>)}
            
            {/* Étape 2 : Aperçu et édition de la lettre d'acceptation */}
            {confirmStep === "lettre" && lettrePreview && (
              <div className="space-y-4" data-testid="lettre-preview-step">
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    Relisez et modifiez si besoin le courrier avant sa génération. Le document .docx final sera stocké dans le dossier « DEMANDE DE RÉSILIATION » du contrat.
                  </AlertDescription>
                </Alert>
                
                <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <p className="font-semibold">{lettrePreview.objet}</p>
                  <p className="text-gray-600">{lettrePreview.reference}</p>
                  <p className="text-gray-600">
                    Destinataire : {[lettrePreview.destinataire?.nom, lettrePreview.destinataire?.fonction, lettrePreview.destinataire?.societe].filter(Boolean).join(" — ") || "À compléter dans le document"}
                  </p>
                </div>
                
                <div>
                  <Label>Corps de la lettre (modifiable)</Label>
                  <Textarea
                    data-testid="lettre-corps-textarea"
                    value={lettreCorps}
                    onChange={(e) => setLettreCorps(e.target.value)}
                    rows={14}
                    className="mt-1 text-sm leading-relaxed"
                  />
                </div>
                
                <div>
                  <Label className="mb-2 block">Signataire de la lettre</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input 
                      data-testid="signataire-lettre-nom"
                      placeholder="Nom" 
                      value={signataireLettre.signataire_nom} 
                      onChange={(e) => setSignataireLettre({...signataireLettre, signataire_nom: e.target.value})} 
                    />
                    <Input 
                      data-testid="signataire-lettre-fonction"
                      placeholder="Fonction" 
                      value={signataireLettre.signataire_fonction} 
                      onChange={(e) => setSignataireLettre({...signataireLettre, signataire_fonction: e.target.value})} 
                    />
                    <Input 
                      placeholder="Téléphone" 
                      value={signataireLettre.signataire_tel} 
                      onChange={(e) => setSignataireLettre({...signataireLettre, signataire_tel: e.target.value})} 
                    />
                    <Input 
                      placeholder="Email" 
                      value={signataireLettre.signataire_email} 
                      onChange={(e) => setSignataireLettre({...signataireLettre, signataire_email: e.target.value})} 
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Le nom et la fonction apparaissent en signature. Si vous changez le signataire, pensez à ajuster le corps de la lettre si nécessaire.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 flex-shrink-0 border-t pt-3">
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowConfirmModal(false);
                setExtractionResult(null);
                setFilesData([]);
                setSelectedFiles([]);
                setConfirmStep("infos");
                setLettrePreview(null);
                setLettreCorps("");
                setSignataireLettre(DEFAULT_SIGNATAIRE);
              }} 
              disabled={confirming}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            {confirmStep === "infos" ? (
              <Button 
                data-testid="preview-lettre-btn"
                onClick={handlePreviewLettre} 
                disabled={loadingPreview}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loadingPreview ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Aperçu de la lettre
              </Button>
            ) : (
              <>
                <Button 
                  data-testid="back-to-infos-btn"
                  variant="outline" 
                  onClick={() => setConfirmStep("infos")} 
                  disabled={confirming}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  data-testid="confirm-resiliation-btn"
                  onClick={handleConfirm} 
                  disabled={confirming}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {confirming ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Valider la résiliation
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default { ResiliationClientCard, ResiliationClientUploadContracts };
